import React, { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { CONSENT_ROUTE, isConsentGateExempt } from '../../config/consentGate';

interface ProtectedRouteProps {
  children: ReactElement;
}

/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication. If the user is not authenticated,
 * they will be redirected to the login page. The intended destination is preserved
 * so the user can be redirected back after successful login.
 * 
 * @example
 * ```tsx
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <Dashboard />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, hasActiveRole, requiresConsent } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login and save intended destination
  if (!isAuthenticated) {
    // Save the current location to redirect back after login
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (!hasActiveRole) {
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/select-role" replace />;
  }

  // Mandatory consent gate (Terms + Privacy Policy at the version in force): like the forced
  // password change, nothing protected is reachable until the user has accepted them.
  if (requiresConsent && !isConsentGateExempt(location.pathname)) {
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to={CONSENT_ROUTE} replace />;
  }

  // User is authenticated, render the protected content
  return children;
};

export default ProtectedRoute;
